import type { DesignCanvas, Frame, ImageLayer } from '../document/types.ts'
import { createImageLayer, defaultConstraints } from '../document/defaults.ts'

/** Relative Slots (0..1 der Trim-Box). Gutter wird beim Umrechnen in mm abgezogen. */
export type CollageLayout = {
  id: string
  name: string
  slots: Array<{ x: number; y: number; w: number; h: number }>
}

function grid(rows: number, cols: number): CollageLayout['slots'] {
  const slots: CollageLayout['slots'] = []
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) slots.push({ x: c / cols, y: r / rows, w: 1 / cols, h: 1 / rows })
  return slots
}

export const COLLAGE_LAYOUTS: CollageLayout[] = [
  { id: '1', name: 'Ein Bild', slots: grid(1, 1) },
  { id: '2h', name: '2 nebeneinander', slots: grid(1, 2) },
  { id: '2v', name: '2 untereinander', slots: grid(2, 1) },
  { id: '3', name: '3 nebeneinander', slots: grid(1, 3) },
  { id: '1+2', name: 'Groß links, 2 rechts', slots: [{ x: 0, y: 0, w: 0.5, h: 1 }, { x: 0.5, y: 0, w: 0.5, h: 0.5 }, { x: 0.5, y: 0.5, w: 0.5, h: 0.5 }] },
  { id: '2+1', name: '2 links, groß rechts', slots: [{ x: 0, y: 0, w: 0.5, h: 0.5 }, { x: 0, y: 0.5, w: 0.5, h: 0.5 }, { x: 0.5, y: 0, w: 0.5, h: 1 }] },
  { id: '4', name: '4er Raster', slots: grid(2, 2) },
  { id: '1+3', name: 'Groß oben, 3 unten', slots: [{ x: 0, y: 0, w: 1, h: 0.62 }, { x: 0, y: 0.62, w: 1 / 3, h: 0.38 }, { x: 1 / 3, y: 0.62, w: 1 / 3, h: 0.38 }, { x: 2 / 3, y: 0.62, w: 1 / 3, h: 0.38 }] },
  { id: '6', name: '6er Raster', slots: grid(2, 3) },
  { id: '9', name: '9er Raster', slots: grid(3, 3) },
]

export function collageLayoutById(id: string): CollageLayout | null {
  return COLLAGE_LAYOUTS.find((l) => l.id === id) ?? null
}

/**
 * Relative Slots -> Rahmen in mm auf der Trim-Box, mit Gutter zwischen den Slots und einem
 * halben Gutter zum Rand. Slots, die am Rand liegen, werden bei `bleedToEdge` bis in den
 * Beschnitt gezogen (randlose Collage).
 */
export function slotsToFrames(slots: CollageLayout['slots'], canvas: Pick<DesignCanvas, 'widthMm' | 'heightMm' | 'bleedMm'>, gutterMm = 3, bleedToEdge = true): Frame[] {
  const W = canvas.widthMm
  const H = canvas.heightMm
  const g = Math.max(0, gutterMm)
  const eps = 1e-6
  return slots.map((s) => {
    let x = s.x * W + g / 2
    let y = s.y * H + g / 2
    let w = s.w * W - g
    let h = s.h * H - g
    if (bleedToEdge) {
      if (s.x <= eps) { x = -canvas.bleedMm; w += g / 2 + canvas.bleedMm }
      if (s.y <= eps) { y = -canvas.bleedMm; h += g / 2 + canvas.bleedMm }
      if (s.x + s.w >= 1 - eps) w += g / 2 + canvas.bleedMm
      if (s.y + s.h >= 1 - eps) h += g / 2 + canvas.bleedMm
    }
    return { xMm: x, yMm: y, wMm: Math.max(0, w), hMm: Math.max(0, h), rotationDeg: 0 }
  })
}

/** Platzhalter-Bildlayer für ein Layout (Slot-Reihenfolge = slotIndex). */
export function layoutToLayers(layout: CollageLayout, canvas: DesignCanvas, gutterMm = 3, idPrefix = 'slot'): ImageLayer[] {
  return slotsToFrames(layout.slots, canvas, gutterMm).map((frame, i) =>
    createImageLayer(`${idPrefix}${i + 1}`, frame, {
      source: { kind: 'placeholder', slotIndex: i },
      constraints: defaultConstraints({ role: 'slot', optional: false }),
    }),
  )
}
